import { FC, ReactNode, useState } from "react";

import { LOGO_HEADER_SIZE, MOBILE_OVERLAY_ROUTE_QUERY_PARAM } from "components/constants";
import { BugReportModal } from "components/header/BugReportModal";
import { SectionSelect } from "components/header/SectionSelect";
import { useIntercomWithDefaults } from "components/intercom/use-intercom-with-defaults";
import { SIDEBAR_PANEL_ICONS } from "helpers/sidebar";
import { t } from "i18n/i18n";
import { Button } from "mds/components/Button";
import { HorizontalDivider } from "mds/components/HorizontalDivider";
import { LegacyButton } from "mds/components/LegacyButton";
import { ListItem } from "mds/components/ListItem";
import {
  ChatStatusIcon,
  CircleQuestionIcon,
  CrossRemoveIcon,
  ForumLogoIcon,
  HelpBugIcon,
  HomeHouseIcon,
  TextBooksIcon,
} from "mds/icons";
import { useCapabilities } from "store/capability-hooks";
import { useAppDispatch, useAppSelector } from "store/index";
import { selectCurrentSectionId, selectQueryString } from "store/selectors";
import { logoutAndClearCaches } from "utils/auth";
import { useAv } from "utils/av";
import { useHomeUrl } from "utils/hooks/use-home-url";
import { useQueryParamsManager } from "utils/routing";
import {
  INSTRUCTOR_FORUM_USER_GUIDE_URL,
  STUDENT_FORUM_USER_GUIDE_URL,
  sectionHomeUrl,
  useViewLevel,
} from "utils/urls";
import { isIntercomEnabled } from "utils/waffle-flags";

type MenuListItemProps = {
  icon: ReactNode;
  text: string;
  overlayRoute: string | null;
  baseUrl?: string;
  onClick?: () => void;
};

const MenuListItem: FC<MenuListItemProps> = ({ icon, text, overlayRoute, baseUrl, onClick }) => {
  const queryString = useAppSelector(selectQueryString);

  // Generates a link that sets `overlay=<route>` in the URL query params,
  // but otherwise preserves the current URL. Returns the new URL.
  const mobileOverlayRouteLink = (route: string | null): string | undefined => {
    if (!route && !baseUrl) return undefined;
    // We don't want to change the object returned from the selector so we clone it.
    const newParams = new URLSearchParams(queryString);
    if (route) {
      newParams.set(MOBILE_OVERLAY_ROUTE_QUERY_PARAM, route);
    } else {
      newParams.delete(MOBILE_OVERLAY_ROUTE_QUERY_PARAM);
    }
    return `${baseUrl || window.location.pathname}?${newParams.toString()}`;
  };

  return (
    <ListItem
      className="h-[60px]"
      kind="primary"
      to={mobileOverlayRouteLink(overlayRoute)}
      onClick={onClick}
    >
      <div className="flex items-center gap-4">
        {icon}
        <span className="font-medium">{text}</span>
      </div>
    </ListItem>
  );
};

export const MobileMainMenu: FC = () => {
  const dispatch = useAppDispatch();
  const viewLevel = useViewLevel();
  const { deleteQueryParams } = useQueryParamsManager();
  const homeUrl = useHomeUrl();

  const { canAuthorSection } = useCapabilities();
  const currentSectionId = useAppSelector(selectCurrentSectionId);
  const { showIntercom } = useIntercomWithDefaults();

  const { isVirtualClassActive } = useAv();

  const [showBugReportModal, setShowBugReportModal] = useState(false);

  return (
    <div className="flex h-full flex-col bg-white">
      <div className="flex items-center justify-between border-b border-black-tint-90 px-4 py-2">
        <div className="flex h-9 items-center">
          <ForumLogoIcon width={LOGO_HEADER_SIZE} />
        </div>
        <Button
          className="ml-2 border-0 bg-transparent text-black"
          kind="squareLegacy"
          title={t("common.close")}
          isSquare
          onClick={() => deleteQueryParams([MOBILE_OVERLAY_ROUTE_QUERY_PARAM])}
        >
          <CrossRemoveIcon size="l" />
        </Button>
      </div>

      <div className="page flex flex-col justify-between">
        <div>
          <SectionSelect kind="secondary" />

          <div className="mt-4">
            {!viewLevel.home && (
              <MenuListItem
                baseUrl={homeUrl}
                icon={<HomeHouseIcon />}
                overlayRoute={null}
                text={t("main_header.home")}
              />
            )}

            {viewLevel.page && (
              <MenuListItem
                baseUrl={sectionHomeUrl(currentSectionId)}
                icon={<TextBooksIcon />}
                overlayRoute={null}
                text={t("main_header.back_to_section")}
              />
            )}

            {currentSectionId && (
              <MenuListItem
                icon={<SIDEBAR_PANEL_ICONS.Planner />}
                overlayRoute="planner"
                text={t("content_sidebar.title.Planner")}
              />
            )}

            <MenuListItem
              icon={<SIDEBAR_PANEL_ICONS.AiAssistant />}
              overlayRoute="ai"
              text={t("content_sidebar.title.AiAssistant")}
            />

            {currentSectionId && (
              <MenuListItem
                icon={<SIDEBAR_PANEL_ICONS.Users />}
                overlayRoute="users"
                text={t("content_sidebar.title.Users")}
              />
            )}

            <MenuListItem
              icon={<SIDEBAR_PANEL_ICONS.Profile />}
              overlayRoute="profile"
              text={t("content_sidebar.title.Profile")}
            />

            <MenuListItem
              icon={<HelpBugIcon />}
              overlayRoute={null}
              text={t("help_center.report_problem")}
              onClick={() => setShowBugReportModal(true)}
            />

            <MenuListItem
              icon={<CircleQuestionIcon />}
              overlayRoute={null}
              text={t("help_center.forum_user_guide")}
              onClick={() =>
                window.open(
                  canAuthorSection ? INSTRUCTOR_FORUM_USER_GUIDE_URL : STUDENT_FORUM_USER_GUIDE_URL,
                  "_blank",
                  "noopener",
                )
              }
            />

            {isVirtualClassActive && (
              <MenuListItem
                icon={<SIDEBAR_PANEL_ICONS.ClassChat />}
                overlayRoute="chat"
                text={t("content_sidebar.title.ClassChat")}
              />
            )}

            {isIntercomEnabled() && (
              <MenuListItem
                icon={<ChatStatusIcon statusColor="green" />}
                overlayRoute={null}
                text={t("help_center.contact_support")}
                onClick={() => showIntercom()}
              />
            )}

            <HorizontalDivider />
          </div>
        </div>

        {/* TODO: Lots of mobile buttons are full-width, we should have a prop for that */}
        <div>
          <LegacyButton
            className="my-4 w-full !max-w-full"
            eventName="Mobile menu - logout"
            kind="secondary"
            onClick={() => {
              logoutAndClearCaches(dispatch);
            }}
          >
            {t("profile_panel.logout")}
          </LegacyButton>
        </div>
      </div>

      {showBugReportModal && <BugReportModal onClose={() => setShowBugReportModal(false)} />}
    </div>
  );
};
